import { createPortal } from 'react-dom';

/**
 * Check if the question has custom distractor rationale
 * @param {Object} questionMetadata - The question metadata object from learnosity
 * @returns {boolean} - Whether the question has custom distractor rationale
 */
export const hasCustomDistractorRationale = (questionMetadata) =>
  !!questionMetadata?.custom_distractor_rationale_response_level;

/**
 * Extract the props for the CustomDistractorRationale component from the metadata and response
 * @param {Object} activeQuestion - The active question object from learnosity
 * @param {*} questionResponse - The question response object from learnosity
 * @param {*} questionMetadata - The question metadata object from learnosity
 * @returns {Object} - The props for the CustomDistractorRationale component
 */
export const getCustomDistractorRationaleProps = (
  activeQuestion,
  questionResponse,
  questionMetadata,
) => {
  if (!activeQuestion || !questionResponse) {
    return {
      isCorrectOption: [],
      optionNumber: [],
      distractorContent: [],
    };
  }

  const learnosityCorrectOptions =
    activeQuestion.getQuestion()?.validation?.valid_response?.value;

  const { value: selectedOptions } = questionResponse;
  const { custom_distractor_rationale_response_level: customDistractors } =
    questionMetadata;

  // Learnosity might be returning the selected options with some letters, eg: 'i1'
  const optionsValue =
    activeQuestion.getProcessedOptions()?.map((option) => option.value) || [];
  selectedOptions?.forEach((option, index) => {
    selectedOptions[index] = String(optionsValue.indexOf(option));
  });

  // Learnosity might be returning the correct options with some letters, eg: 'i1'
  const correctOptions = learnosityCorrectOptions?.map((option) =>
    String(optionsValue.indexOf(option)),
  );

  return {
    correctOptions,
    selectedOptions,
    customDistractors,
  };
};

/**
 * Injects a custom distractor rationale into the learnosity distractor wrapper
 * @param {React.component} children - The children to render in the learnosity distractor wrapper
 * @returns {React.component} - The children wrapped in a portal to the learnosity distractor wrapper
 */
export const injectCustomDistractorRationale = (
  children,
  questionReference,
) => {
  const learnosityDistractorWrapperSelector = `div[data-reference="${questionReference}"] div.lrn_distractor_rationale_wrapper`;

  if (!document.querySelector(learnosityDistractorWrapperSelector)) {
    return null;
  }
  const learnosityDistractorWrapperElement = document.querySelector(
    learnosityDistractorWrapperSelector,
  );

  return createPortal(children, learnosityDistractorWrapperElement);
};

export const isCustomDistractorRationaleSaved = (
  questionReference,
  customDistractorsProps,
) => !!customDistractorsProps[questionReference];

/**
 * Convert the question number to a letter
 * @param number - The number to convert to a letter
 * @returns - The letter corresponding to the number
 */
export const convertQuestionNumberToLetter = (number) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return alphabet.charAt(Number(number));
};

/**
 * Convert the learnosity generated distractor rationale number to a letter
 */
export const convertLearnosityDistractorRationaleNumberToLetter = (
  itemReference,
  selectedOptions,
) => {
  const questionElement = document.querySelector(
    `div[data-reference="${itemReference}"]`,
  );
  const DISTRACTOR_NUMBER_CONTAINER_SELECTOR =
    'div.lrn_validation_stem_numeration';
  const distractorNumberContainers = questionElement.querySelectorAll(
    DISTRACTOR_NUMBER_CONTAINER_SELECTOR,
  );
  distractorNumberContainers.forEach((container, index) => {
    const numberContainer = container;
    numberContainer.innerText = convertQuestionNumberToLetter(
      selectedOptions[index],
    );
  });
};
